import { useState, useCallback } from "react";
import { ApprovalCountsEntity, GetApprovalCountsUseCase } from "../../domain";
import { ApprovalRepositoryImpl } from "../../data/repositories/ApprovalRepositoryImpl";

// Create repository instance (singleton)
const approvalRepository = new ApprovalRepositoryImpl();

// Create use case
const getApprovalCountsUseCase = new GetApprovalCountsUseCase(
  approvalRepository,
);

export interface UseApprovalCountsReturn {
  counts: ApprovalCountsEntity | null;
  myRequestsCounts: ApprovalCountsEntity | null;
  myRequestsCount: number;
  loading: boolean;
  error: string | null;
  fetchCounts: () => Promise<void>;
  refresh: () => Promise<void>;
  clearError: () => void;
}

export const useApprovalCounts = (): UseApprovalCountsReturn => {
  const [counts, setCounts] = useState<ApprovalCountsEntity | null>(null);
  const [myRequestsCounts, setMyRequestsCounts] =
    useState<ApprovalCountsEntity | null>(null);
  const [myRequestsCount, setMyRequestsCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch approval counts and my requests counts.
   * Uses Promise.allSettled so one failure does not leave the other count at 0.
   */
  const fetchCounts = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    const [approvalsSettled, myRequestsSettled] = await Promise.allSettled([
      approvalRepository.getCounts("approvals"),
      approvalRepository.getCounts("my-requests"),
    ]);

    if (approvalsSettled.status === "fulfilled") {
      setCounts(approvalsSettled.value);
    }
    if (myRequestsSettled.status === "fulfilled") {
      const myRequestsCountsResult = myRequestsSettled.value;
      setMyRequestsCounts(myRequestsCountsResult);
      const total = Object.values(myRequestsCountsResult).reduce(
        (sum, entity) =>
          sum + entity.pending + entity.approved + entity.rejected,
        0,
      );
      setMyRequestsCount(total);
    }

    const errors: string[] = [];
    if (approvalsSettled.status === "rejected") {
      console.error("Error fetching approval counts:", approvalsSettled.reason);
      errors.push(String(approvalsSettled.reason?.message ?? approvalsSettled.reason));
    }
    if (myRequestsSettled.status === "rejected") {
      console.error("Error fetching my-requests counts:", myRequestsSettled.reason);
      errors.push(String(myRequestsSettled.reason?.message ?? myRequestsSettled.reason));
    }
    if (errors.length > 0) {
      setError(errors.join("; ") || "Failed to load approval counts");
    }

    setLoading(false);
  }, []);

  /**
   * Refresh counts
   */
  const refresh = useCallback(async (): Promise<void> => {
    await fetchCounts();
  }, [fetchCounts]);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    counts,
    myRequestsCounts,
    myRequestsCount,
    loading,
    error,
    fetchCounts,
    refresh,
    clearError,
  };
};
